import api from './api';
import type { PaginatedResponse } from '@/types';

// Types
export interface Country {
  id: string;
  name: string;
  code: string;
  code_alpha2: string;
  phone_code: string;
  currency_code: string;
  currency_symbol: string;
  timezone: string;
  date_format?: string;
  language_code?: string;
  domain?: string;
  status: 'active' | 'paused' | 'blocked';
  regulatory_body?: string;
  tax_rate?: number;
  kyc_required?: boolean;
  kyc_documents?: string[];
  features?: Record<string, boolean>;
  states_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface State {
  id: string;
  name: string;
  code: string;
  gst_code?: string;
  zone: 'A' | 'B' | 'C';
  country: string;
  country_name?: string;
  is_active: boolean;
  cities_count?: number;
  rtos_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface City {
  id: string;
  name: string;
  code?: string;
  tier: 'metro' | 'tier1' | 'tier2' | 'tier3' | 'rural';
  state: string;
  state_name?: string;
  country_name?: string;
  latitude?: number;
  longitude?: number;
  is_active: boolean;
  pincodes_count?: number;
  rtos_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Pincode {
  id: string;
  code: string;
  area_name?: string;
  post_office?: string;
  delivery_status: 'delivery' | 'non_delivery';
  city: string;
  city_name?: string;
  state_name?: string;
  country_name?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface RTO {
  id: string;
  code: string;
  name: string;
  zone: 'A' | 'B' | 'C';
  state: string;
  state_name?: string;
  city?: string;
  city_name?: string;
  address?: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Lookup types
export interface CountryLookup {
  id: string;
  name: string;
  code: string;
}

export interface StateLookup {
  id: string;
  name: string;
  code: string;
  country: string;
}

export interface CityLookup {
  id: string;
  name: string;
  code?: string;
  state: string;
}

export interface PincodeLookup {
  id: string;
  code: string;
  area_name?: string;
  city: string;
  city_name?: string;
  /**
   * M-04: the State's id. Optional because a cached `lookup`/`search` payload
   * produced before the field was added carries only the name -- a consumer
   * must be able to fall back to matching that name against the states list.
   */
  state?: string;
  state_name?: string;
}

export interface RTOLookup {
  id: string;
  code: string;
  name: string;
  state: string;
}

// Filter types
export interface CountryFilters {
  status?: string;
  search?: string;
}

export interface StateFilters {
  country?: string;
  zone?: string;
  is_active?: boolean;
  search?: string;
}

export interface CityFilters {
  state?: string;
  country?: string;
  tier?: string;
  is_active?: boolean;
  search?: string;
}

export interface PincodeFilters {
  city?: string;
  state?: string;
  delivery_status?: string;
  is_active?: boolean;
  /** Exact match against the free-text `post_office` field (item 20). */
  post_office?: string;
  search?: string;
}

export interface RTOFilters {
  state?: string;
  city?: string;
  zone?: string;
  is_active?: boolean;
  search?: string;
}

// Request types
export interface CountryRequest {
  name: string;
  code: string;
  code_alpha2: string;
  phone_code: string;
  currency_code?: string;
  currency_symbol?: string;
  timezone?: string;
  date_format?: string;
  language_code?: string;
  domain?: string;
  status?: string;
  regulatory_body?: string;
  tax_rate?: number;
  kyc_required?: boolean;
  kyc_documents?: string[];
  features?: Record<string, boolean>;
}

export interface StateRequest {
  country: string;
  name: string;
  code: string;
  gst_code?: string;
  zone?: string;
  is_active?: boolean;
}

export interface CityRequest {
  state: string;
  name: string;
  code?: string;
  tier?: string;
  latitude?: number;
  longitude?: number;
  is_active?: boolean;
}

export interface PincodeRequest {
  city: string;
  code: string;
  area_name?: string;
  post_office?: string;
  delivery_status?: string;
  is_active?: boolean;
}

export interface RTORequest {
  state: string;
  city?: string;
  code: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  zone?: string;
  is_active?: boolean;
}

// Helper function to build query params
const buildParams = <T extends object>(page: number, filters?: T): URLSearchParams => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return params;
};

export const geographyService = {
  // Countries
  async getCountries(page: number = 1, filters?: CountryFilters): Promise<PaginatedResponse<Country>> {
    const params = buildParams(page, filters);
    const response = await api.get<PaginatedResponse<Country>>(`/geography/countries/?${params}`);
    return response.data;
  },

  async getCountry(id: string): Promise<Country> {
    const response = await api.get<Country>(`/geography/countries/${id}/`);
    return response.data;
  },

  async createCountry(data: CountryRequest): Promise<Country> {
    const response = await api.post<Country>('/geography/countries/', data);
    return response.data;
  },

  async updateCountry(id: string, data: Partial<CountryRequest>): Promise<Country> {
    const response = await api.patch<Country>(`/geography/countries/${id}/`, data);
    return response.data;
  },

  async deleteCountry(id: string): Promise<void> {
    await api.delete(`/geography/countries/${id}/`);
  },

  async getCountryLookup(): Promise<CountryLookup[]> {
    const response = await api.get<CountryLookup[]>('/geography/countries/lookup/');
    return response.data;
  },

  async activateCountry(id: string): Promise<Country> {
    const response = await api.post<Country>(`/geography/countries/${id}/activate/`);
    return response.data;
  },

  async pauseCountry(id: string): Promise<Country> {
    const response = await api.post<Country>(`/geography/countries/${id}/pause/`);
    return response.data;
  },

  async blockCountry(id: string): Promise<Country> {
    const response = await api.post<Country>(`/geography/countries/${id}/block/`);
    return response.data;
  },

  // States
  async getStates(page: number = 1, filters?: StateFilters): Promise<PaginatedResponse<State>> {
    const params = buildParams(page, filters);
    const response = await api.get<PaginatedResponse<State>>(`/geography/states/?${params}`);
    return response.data;
  },

  async getState(id: string): Promise<State> {
    const response = await api.get<State>(`/geography/states/${id}/`);
    return response.data;
  },

  async createState(data: StateRequest): Promise<State> {
    const response = await api.post<State>('/geography/states/', data);
    return response.data;
  },

  async updateState(id: string, data: Partial<StateRequest>): Promise<State> {
    const response = await api.patch<State>(`/geography/states/${id}/`, data);
    return response.data;
  },

  async deleteState(id: string): Promise<void> {
    await api.delete(`/geography/states/${id}/`);
  },

  async getStateLookup(countryId?: string): Promise<StateLookup[]> {
    const params = countryId ? `?country=${countryId}` : '';
    const response = await api.get<StateLookup[]>(`/geography/states/lookup/${params}`);
    return response.data;
  },

  async toggleStateActive(id: string): Promise<State> {
    const response = await api.post<State>(`/geography/states/${id}/toggle_active/`);
    return response.data;
  },

  // Cities
  async getCities(page: number = 1, filters?: CityFilters): Promise<PaginatedResponse<City>> {
    const params = buildParams(page, filters);
    const response = await api.get<PaginatedResponse<City>>(`/geography/cities/?${params}`);
    return response.data;
  },

  async getCity(id: string): Promise<City> {
    const response = await api.get<City>(`/geography/cities/${id}/`);
    return response.data;
  },

  async createCity(data: CityRequest): Promise<City> {
    const response = await api.post<City>('/geography/cities/', data);
    return response.data;
  },

  async updateCity(id: string, data: Partial<CityRequest>): Promise<City> {
    const response = await api.patch<City>(`/geography/cities/${id}/`, data);
    return response.data;
  },

  async deleteCity(id: string): Promise<void> {
    await api.delete(`/geography/cities/${id}/`);
  },

  async getCityLookup(stateId?: string): Promise<CityLookup[]> {
    const params = stateId ? `?state=${stateId}` : '';
    const response = await api.get<CityLookup[]>(`/geography/cities/lookup/${params}`);
    return response.data;
  },

  async toggleCityActive(id: string): Promise<City> {
    const response = await api.post<City>(`/geography/cities/${id}/toggle_active/`);
    return response.data;
  },

  // Pincodes
  async getPincodes(page: number = 1, filters?: PincodeFilters): Promise<PaginatedResponse<Pincode>> {
    const params = buildParams(page, filters);
    const response = await api.get<PaginatedResponse<Pincode>>(`/geography/pincodes/?${params}`);
    return response.data;
  },

  async getPincode(id: string): Promise<Pincode> {
    const response = await api.get<Pincode>(`/geography/pincodes/${id}/`);
    return response.data;
  },

  async createPincode(data: PincodeRequest): Promise<Pincode> {
    const response = await api.post<Pincode>('/geography/pincodes/', data);
    return response.data;
  },

  async updatePincode(id: string, data: Partial<PincodeRequest>): Promise<Pincode> {
    const response = await api.patch<Pincode>(`/geography/pincodes/${id}/`, data);
    return response.data;
  },

  async deletePincode(id: string): Promise<void> {
    await api.delete(`/geography/pincodes/${id}/`);
  },

  async searchPincodes(code: string): Promise<{ results: PincodeLookup[] }> {
    const response = await api.get<{ results: PincodeLookup[] }>(`/geography/pincodes/search/?code=${code}`);
    return response.data;
  },

  /**
   * Distinct post office names for the Post Office filter dropdown (item 20).
   * `post_office` is free text -- there is no lookup table the way State/City
   * have one -- so this reads the values actually present, scoped by
   * state/city exactly like `getCityLookup` narrows by state.
   */
  async getPostOfficeLookup(filters?: { state?: string; city?: string }): Promise<string[]> {
    const params = new URLSearchParams();
    if (filters?.state) params.append('state', filters.state);
    if (filters?.city) params.append('city', filters.city);
    const qs = params.toString();
    const response = await api.get<string[]>(`/geography/pincodes/post_offices/${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async togglePincodeActive(id: string): Promise<Pincode> {
    const response = await api.post<Pincode>(`/geography/pincodes/${id}/toggle_active/`);
    return response.data;
  },

  // RTOs
  async getRTOs(page: number = 1, filters?: RTOFilters): Promise<PaginatedResponse<RTO>> {
    const params = buildParams(page, filters);
    const response = await api.get<PaginatedResponse<RTO>>(`/geography/rtos/?${params}`);
    return response.data;
  },

  async getRTO(id: string): Promise<RTO> {
    const response = await api.get<RTO>(`/geography/rtos/${id}/`);
    return response.data;
  },

  async createRTO(data: RTORequest): Promise<RTO> {
    const response = await api.post<RTO>('/geography/rtos/', data);
    return response.data;
  },

  async updateRTO(id: string, data: Partial<RTORequest>): Promise<RTO> {
    const response = await api.patch<RTO>(`/geography/rtos/${id}/`, data);
    return response.data;
  },

  async deleteRTO(id: string): Promise<void> {
    await api.delete(`/geography/rtos/${id}/`);
  },

  // M-04: `city` narrows the list to the offices of one city, which is what
  // the Add Policy RTO block needs to decide "this city has exactly one RTO".
  // `RTOViewSet.get_queryset` has always read the param; nothing here sent it.
  async getRTOLookup(stateId?: string, cityId?: string): Promise<RTOLookup[]> {
    const search = new URLSearchParams();
    if (stateId) search.append('state', stateId);
    if (cityId) search.append('city', cityId);
    const qs = search.toString();
    const response = await api.get<RTOLookup[]>(`/geography/rtos/lookup/${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async searchRTOs(code: string): Promise<{ results: RTOLookup[] }> {
    const response = await api.get<{ results: RTOLookup[] }>(`/geography/rtos/search/?code=${code}`);
    return response.data;
  },

  async toggleRTOActive(id: string): Promise<RTO> {
    const response = await api.post<RTO>(`/geography/rtos/${id}/toggle_active/`);
    return response.data;
  },
};
